import { useCallback, useMemo, useRef } from 'react';
import { SortOrder } from 'antd/es/table/interface';
import { DelimitedArrayParam, useQueryParam } from 'use-query-params';

import { SortOrder as APISortOrder } from 'codegen';
import { useAppState } from 'containers/ApplicationStateContext';

const mapSorterOrder = (order?: SortOrder) => {
  switch (order) {
    case 'ascend':
      return APISortOrder.Asc;
    case 'descend':
      return APISortOrder.Desc;
    default:
      return order;
  }
};

type SorterValue = [string, SortOrder] | undefined;

const useTableSorting = (
  defaultColumnKey?: string,
  defaultColumnOrder?: SortOrder,
  { queryParamName = 'order', storeKey: key }: { queryParamName?: string; storeKey?: string } = {}
) => {
  const sortChanged = useRef(false);

  const [valueParam, setValueParam] = useQueryParam(queryParamName, DelimitedArrayParam) as [
    SorterValue,
    (val: SorterValue) => void
  ];

  const { storedValues, setStoredValue } = useAppState();

  const value: SorterValue = useMemo(
    () => (key ? storedValues[`${key}Sort`] ?? [] : valueParam),
    [key, storedValues, valueParam]
  );

  const setValue = useCallback(
    (value: SorterValue) =>
      key ? setStoredValue({ key: `${key}Sort`, value }) : setValueParam(value),
    [key, setStoredValue, setValueParam]
  );

  const [sortColumnKey, sortOrder] = value || [];

  const handleSortChange = useCallback(
    (newColumnKey?: string | number | null, newSortOrder?: SortOrder) => {
      if (!sortChanged.current && newColumnKey && newSortOrder) {
        sortChanged.current = true;
      }

      if (!newColumnKey || !newSortOrder) {
        setValue(undefined);
        sortChanged.current = false;
        return;
      }

      return setValue([`${newColumnKey}`, newSortOrder]);
    },
    [setValue]
  );

  const getOrderForColumn = useCallback(
    (columnKey: string) => (sortColumnKey === columnKey ? sortOrder : undefined),
    [sortColumnKey, sortOrder]
  );

  return {
    sortColumnKey,
    sortOrder,
    sort:
      sortColumnKey && sortOrder
        ? { key: sortColumnKey, order: mapSorterOrder(sortOrder) }
        : !sortChanged.current && defaultColumnKey && defaultColumnOrder
        ? { key: defaultColumnKey, order: mapSorterOrder(defaultColumnOrder) }
        : undefined,
    handleSortChange,
    getOrderForColumn,
  };
};

export default useTableSorting;
